'use client'

import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { MobileHeader, HeaderDataMobile } from '@quark/header'
import { Button } from '@quark/button'
import { Text } from '@quark/typography'
import { Flex, Container } from '@quark/layout'
import { Avatar } from '@quark/avatar'
import { ArrowRight } from '@quark/icons'
import { logoutUser } from '@services/loginService'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { getModifiedOptions } from '@factories/getGlobalHeaderData'
import { getUrlWithQueryParams } from '@utils/queryParamUtils'
import { Locale } from '@enums/strapiTypesAndOperators'
import { getLanguagePickerOptions } from '@utils/localisation/languagePickerOptions'

interface HeaderMobileProps {
  isLoggedIn: boolean
  userData?: any
  content?: any
  animate?: boolean
  ackoLogo?: string
  isSemPage?: boolean
  setFormSnippet?: (formSnippet: string) => void
  showLoginButton?: boolean
  showFileAClaimButton?: boolean
  selectedLanguage: Locale
  availableLanguages: Locale[]
  defaultLink?: string
}

const HeaderMobile: React.FC<HeaderMobileProps> = ({
  isLoggedIn,
  userData,
  content,
  animate = false,
  ackoLogo,
  isSemPage = false,
  setFormSnippet,
  showLoginButton = true,
  showFileAClaimButton = true,
  selectedLanguage,
  availableLanguages,
  defaultLink = '/',
}) => {
  const config = useEnvVariablesClientConfig()
  const [logoHref, setLogoHref] = useState(defaultLink)

  useEffect(() => {
    setLogoHref(getUrlWithQueryParams(defaultLink))
  }, [defaultLink])

  const handleLogout = useCallback(async () => {
    try {
      await logoutUser({
        env: config?.ACKO_ENV,
      })
    } catch (error) {
      console.error('Logout failed', error)
    } finally {
      window.location.reload()
    }
  }, [config])

  const handleNavClick = useCallback((url: string) => {
    void trackEvent('tap_header_raise_a_claim_cta', {
      page_type: 'seo',
      redirection_url: url,
    })

    if (url && url !== '') window.location.href = getUrlWithQueryParams(url)
  }, [])

  const renderUserData = useMemo(
    () => (
      <Flex
        flexDirection="column"
        onClick={() => {
          window.open(getUrlWithQueryParams('/profile'), '_blank')
        }}>
        <Flex flexDirection={{ sm: 'column', md: 'column', lg: 'row' }}>
          <Container
            backgroundColor="base/elevated"
            padding="spacing-20"
            borderRadius={{ sm: 'radius-3xl', md: 'radius-3xl', lg: 'radius-3xl' }}>
            <Flex alignItems="center" justifyContent="space-between" gap={{ sm: 'spacing-4', md: 'spacing-4' }}>
              <Flex
                alignItems="center"
                gap={{ sm: 'spacing-12', md: 'spacing-12', lg: 'spacing-12' }}
                justifyContent="center">
                <Avatar size={{ sm: 'lg', md: 'lg', lg: 'lg' }} backgroundColor="accent/Violet/Violet-100">
                  <Text
                    fontStyle={{
                      sm: 'um_text-xl/normal',
                      md: 'um_text-xl/normal',
                      lg: 'um_text-base/normal',
                    }}
                    color="base/primary"
                    lineHeight="line-height-0">
                    {userData?.avatar}
                  </Text>
                </Avatar>
                <Flex alignItems="baseline" flexDirection="column" gap="spacing-4">
                  <Text
                    as="div"
                    fontStyle={{
                      sm: 'um_text-base/normal',
                      md: 'um_text-base/normal',
                      lg: 'um_text-base/normal',
                    }}
                    color="base/primary">
                    {userData?.name}
                  </Text>
                  <Text
                    as="div"
                    fontStyle={{
                      sm: 'um_text-sm/medium',
                      md: 'um_text-sm/medium',
                      lg: 'um_text-sm/medium',
                    }}
                    color="base/secondary">
                    {userData?.phone}
                  </Text>
                </Flex>
              </Flex>
              <Text
                as="div"
                fontStyle={{
                  sm: 'um_text-2xl/normal',
                  md: 'um_text-2xl/normal',
                  lg: 'um_text-base/normal',
                }}
                color="base/primary"
                lineHeight="line-height-0">
                <ArrowRight />
              </Text>
            </Flex>
          </Container>
        </Flex>
      </Flex>
    ),
    [userData]
  )

  const renderFooter = useMemo(() => {
    if (!showLoginButton) {
      return null
    }
    return !isLoggedIn || !userData ? (
      <Flex gap="spacing-20">
        <Button
          type="solid"
          size="block"
          onClick={() => {
            window.location.href = getUrlWithQueryParams('/authn/v1/signin')
          }}>
          <Text fontStyle="um_text-base/normal" color="base/inverted primary">
            Login
          </Text>
        </Button>
      </Flex>
    ) : (
      <Flex gap="spacing-20">
        <Button
          type="solid"
          size="block"
          onClick={() => {
            void handleLogout()
          }}>
          <Text fontStyle="um_text-base/normal" color="base/inverted primary">
            Logout
          </Text>
        </Button>
      </Flex>
    )
  }, [isLoggedIn, userData, showLoginButton, handleLogout])

  const headerData: HeaderDataMobile = useMemo(() => {
    const config: HeaderDataMobile = {
      logo: {
        darkUrl: isSemPage ? ackoLogo : content?.darkThemeLogoUrl,
        lightUrl: isSemPage ? ackoLogo : content?.lightThemeLogoUrl,
        props: { href: logoHref },
      },
      options: isSemPage ? [] : getModifiedOptions(content, isLoggedIn, setFormSnippet),
      // @ts-expect-error
      navElements: [
        ...(isSemPage
          ? []
          : showFileAClaimButton
            ? [
                {
                  name: 'Claims',
                  type: 'outlined-button',
                  isExpandable: false,
                  props: {
                    onClick: () => handleNavClick('/myaccount'),
                  },
                },
              ]
            : []),
        ...(availableLanguages.length > 1 ? [getLanguagePickerOptions(selectedLanguage, availableLanguages)] : []),
      ],
    }

    if (userData && isLoggedIn) {
      config.userData = renderUserData
      config.footer = renderFooter
    } else {
      config.footer = renderFooter
    }

    return config
  }, [
    content,
    isLoggedIn,
    setFormSnippet,
    showFileAClaimButton,
    handleNavClick,
    logoHref,
    userData,
    renderUserData,
    renderFooter,
    isSemPage,
    ackoLogo,
  ])
  return (
    <MobileHeader
      headerData={headerData}
      animate={animate}
      haveBackground={!isSemPage}
      showHamburgerIcon={!isSemPage}
    />
  )
}

export default React.memo(HeaderMobile)
